import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, MessageSquarePlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// "Ask your dispensary to join Bud Link" dialog. Lets a patient nominate
// the shop they wish was on Bud Link's live menu (or wish had its full
// menu + sales unlocked). Submissions land in db.dispensary_requests and
// surface in the Admin Outreach tab so the operator can act on them.
//
// Two ways to open:
//   1. <AskDispensaryDialog defaultName="Trulieve Allentown" ... />
//      → button on a dispensary detail page when live_menu === false
//   2. <AskDispensaryDialog>(no defaults)</AskDispensaryDialog>
//      → "Don't see your shop?" footer link
//
// Email is OPTIONAL. The user shouldn't have to give us anything just to
// signal demand — but if they do, we can follow up when the shop joins.

export const AskDispensaryDialog = ({
  open,
  onOpenChange,
  defaultDispensaryName = "",
  defaultDispensarySlug = "",
}) => {
  const [name, setName] = useState(defaultDispensaryName);
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Reset the form whenever the dialog opens so the prior submission isn't
  // sitting there next time. Default name re-applies in case the user
  // re-opens from a different dispensary's page.
  useEffect(() => {
    if (open) {
      setName(defaultDispensaryName);
      setEmail("");
      setZip("");
      setNote("");
      setDone(false);
    }
  }, [open, defaultDispensaryName]);

  const submit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/dispensary-requests", {
        dispensary_name: name.trim(),
        dispensary_slug: defaultDispensarySlug || undefined,
        email: email.trim() || undefined,
        zip: zip.trim() || undefined,
        note: note.trim() || undefined,
      });
      setDone(true);
      toast.success("Thanks — we'll reach out to them.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm rounded-2xl"
        data-testid="ask-dispensary-dialog"
      >
        <DialogTitle className="font-serif text-2xl flex items-start gap-2">
          <MessageSquarePlus className="w-5 h-5 mt-1 text-emerald-300 shrink-0" />
          {done ? "We're on it" : "Ask your dispensary to join"}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
          {done
            ? "We'll reach out to them directly. The more patients ask, the faster they connect."
            : "Tell us which shop you want fully on Bud Link — live menu, real-time stock, sales, and best-price-near-you. We'll do the outreach."}
        </DialogDescription>

        {!done && (
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-foreground/60 mb-1 block">
                Dispensary name <span className="text-rose-400">*</span>
              </label>
              <Input
                data-testid="ask-dispensary-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cresco Market — Lancaster"
                className="h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-foreground/60 mb-1 block">Your ZIP</label>
                <Input
                  data-testid="ask-dispensary-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="optional"
                  className="h-11"
                  maxLength={5}
                  inputMode="numeric"
                />
              </div>
              <div>
                <label className="text-xs text-foreground/60 mb-1 block">Your email</label>
                <Input
                  data-testid="ask-dispensary-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="optional"
                  className="h-11"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-foreground/60 mb-1 block">
                Anything we should mention?
              </label>
              <Input
                data-testid="ask-dispensary-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="optional — e.g. ‘I'm a regular here'"
                className="h-11"
              />
            </div>
            <Button
              data-testid="ask-dispensary-submit"
              onClick={submit}
              disabled={loading || !name.trim()}
              className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Send the request"
              )}
            </Button>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center pt-1">
              No spam · Email is optional · 21+ only
            </p>
          </div>
        )}
        {done && (
          <div className="flex justify-center pt-2">
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AskDispensaryDialog;
